/**
 * TimesheetMovesAudit — R148p · Feb 15 2026 · Filters added R150 · Feb 2026
 *
 * HR-facing audit trail of every bulk timesheet move (who moved which
 * entries, from → to task, reason). Read-only.
 *
 * Route: /app/timesheet/moves
 * Backend: GET /workspaces/{ws}/timesheet-moves?limit=300&since=ISO
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { fmtRelative, fmtDateTime } from "@/lib/format";
import { Archive, ArrowRight, Clock, X } from "lucide-react";

export default function TimesheetMovesAudit() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  // R150 · Filters — date-range (moved_at) + entry-owner (dropdown of
  // distinct owners in the current page). Everything client-side so
  // switching filters is instant.
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [moverFilter, setMoverFilter] = useState("");
  const [reasonQuery, setReasonQuery] = useState("");

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/timesheet-moves`, { params: { limit: 500 } });
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        const msg = apiErrorMessage(e, "Failed to load audit log");
        setError(msg);
        if (e?.response?.status !== 403) toast.error(msg);
      } finally { setBusy(false); }
    })();
  }, [activeWorkspaceId]);

  const owners = useMemo(() => {
    const set = new Map();
    for (const r of rows) if (r.entry_owner_id) set.set(r.entry_owner_id, r.entry_owner_name || r.entry_owner_id);
    return Array.from(set.entries()).sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [rows]);
  const movers = useMemo(() => {
    const set = new Map();
    for (const r of rows) if (r.moved_by) set.set(r.moved_by, r.moved_by_name || r.moved_by);
    return Array.from(set.entries()).sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = reasonQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (ownerFilter && r.entry_owner_id !== ownerFilter) return false;
      if (moverFilter && r.moved_by !== moverFilter) return false;
      if (fromDate && (!r.moved_at || r.moved_at.slice(0, 10) < fromDate)) return false;
      if (toDate && (!r.moved_at || r.moved_at.slice(0, 10) > toDate)) return false;
      if (q) {
        const hay = [
          r.reason, r.from_task_title, r.to_task_title, r.moved_by_name, r.entry_owner_name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, ownerFilter, moverFilter, fromDate, toDate, reasonQuery]);

  const totalHours = filtered.reduce((s, r) => s + (Number(r.hours) || 0), 0);
  const anyFilter = !!(ownerFilter || moverFilter || fromDate || toDate || reasonQuery);
  const clearFilters = () => {
    setOwnerFilter(""); setMoverFilter(""); setFromDate(""); setToDate(""); setReasonQuery("");
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="TIMESHEET · AUDIT"
        title="Timesheet move audit"
        description="Every bulk move of a time entry from one task to another. HR-only. Read-only forensic log."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        {busy && (
          <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="tsm-loading">
            Loading audit log…
          </div>
        )}
        {!busy && error && (
          <div className="border border-rose-300 bg-rose-50 text-rose-800 p-6 text-center text-sm" data-testid="tsm-error">
            {error}
          </div>
        )}
        {!busy && !error && rows.length === 0 && (
          <div className="border border-border p-10 text-center" data-testid="tsm-empty">
            <Archive className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
            <div className="text-sm font-semibold">No moves recorded yet</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              As soon as someone uses <b>Move to another task…</b> in the Daily Plan list view, every affected entry lands here with the reason.
            </p>
          </div>
        )}
        {!busy && !error && rows.length > 0 && (
          <>
            {/* Filter bar */}
            <div className="border border-border bg-muted/30 px-3 py-2 grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid="tsm-filters">
              <div className="md:col-span-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-0.5">From</div>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full text-xs px-2 py-1 bg-background border border-border"
                  data-testid="tsm-filter-from"
                />
              </div>
              <div className="md:col-span-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-0.5">To</div>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full text-xs px-2 py-1 bg-background border border-border"
                  data-testid="tsm-filter-to"
                />
              </div>
              <div className="md:col-span-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-0.5">Entry owner</div>
                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="w-full text-xs px-2 py-1 bg-background border border-border"
                  data-testid="tsm-filter-owner"
                >
                  <option value="">— Anyone —</option>
                  {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </div>
              <div className="md:col-span-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-0.5">Moved by</div>
                <select
                  value={moverFilter}
                  onChange={(e) => setMoverFilter(e.target.value)}
                  className="w-full text-xs px-2 py-1 bg-background border border-border"
                  data-testid="tsm-filter-mover"
                >
                  <option value="">— Anyone —</option>
                  {movers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </div>
              <div className="md:col-span-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-0.5">Search reason</div>
                <input
                  value={reasonQuery}
                  onChange={(e) => setReasonQuery(e.target.value)}
                  placeholder="task, reason…"
                  className="w-full text-xs px-2 py-1 bg-background border border-border"
                  data-testid="tsm-filter-reason"
                />
              </div>
              <div className="md:col-span-1 flex items-end">
                <button
                  type="button"
                  onClick={clearFilters}
                  disabled={!anyFilter}
                  className="w-full text-[11px] px-2 py-1 border border-border bg-background hover:bg-muted disabled:opacity-40 inline-flex items-center justify-center gap-1"
                  data-testid="tsm-filter-clear"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              </div>
            </div>

            <div className="border border-border bg-muted/40 px-4 py-2 flex items-center gap-4 text-[11px]" data-testid="tsm-summary">
              <span><b className="text-base">{filtered.length}</b> {anyFilter ? "match" : "moves"}{filtered.length === 1 ? "" : anyFilter ? "es" : ""}</span>
              <span><b className="text-base">{totalHours.toFixed(2)}</b> total hours corrected</span>
              {anyFilter && filtered.length !== rows.length && (
                <span className="text-muted-foreground">of {rows.length} total</span>
              )}
            </div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px] w-40">Moved</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px] w-40">By</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px] w-40">Entry owner</th>
                    <th className="text-right px-3 py-2 font-semibold uppercase tracking-widest text-[10px] w-20">Hours</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px] w-24">Entry date</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px]">From task → To task</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px]">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground text-xs" data-testid="tsm-no-match">
                        No moves match the current filters.
                      </td>
                    </tr>
                  ) : filtered.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/20" data-testid={`tsm-row-${r.id}`}>
                      <td className="px-3 py-2 align-top" title={r.moved_at}>
                        <div className="flex items-center gap-1"><Clock className="w-3 h-3 text-muted-foreground" /> {fmtRelative(r.moved_at)}</div>
                        <div className="text-[10px] text-muted-foreground">{fmtDateTime(r.moved_at)}</div>
                      </td>
                      <td className="px-3 py-2 align-top">{r.moved_by_name}</td>
                      <td className="px-3 py-2 align-top text-muted-foreground">{r.entry_owner_name}</td>
                      <td className="px-3 py-2 align-top text-right font-mono">{Number(r.hours || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 align-top text-muted-foreground tabular-nums">{r.date || "—"}</td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex items-start gap-2">
                          <span className="text-rose-700 line-through truncate max-w-[24ch]" title={r.from_task_title || r.from_task_id}>
                            {r.from_task_title || r.from_task_id || "—"}
                          </span>
                          <ArrowRight className="w-3 h-3 mt-1 text-muted-foreground shrink-0" />
                          <span className="text-emerald-700 font-semibold truncate max-w-[24ch]" title={r.to_task_title || r.to_task_id}>
                            {r.to_task_title || r.to_task_id || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-foreground/80 whitespace-pre-wrap">{r.reason || <em className="text-muted-foreground">—</em>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
